import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './i18n/config'; // Initialize i18n
import i18n from './i18n/config';
import HomePage from './pages/HomePage';
import CreateTripV2 from './pages/CreateTripV2';
import Plans from './pages/Plans';
import PlanDetail from './pages/PlanDetail';
import Settings from './pages/Settings';
import Groups from './pages/Groups';
import Inbox from './pages/Inbox';
import InboxContactUs from './pages/InboxContactUs';
import LuggageTags from './pages/LuggageTags';
import LuggageTagsPrint from './pages/LuggageTagsPrint';
import PlanPrint from './pages/PlanPrint';
import MapTest from './components/MapTest';
import DateTimeTest from './pages/DateTimeTest';
import ImageSearchTest from './pages/ImageSearchTest';
import DebugSettings from './pages/DebugSettings';
import BackToTop from './components/BackToTop';
import GoogleAnalytics from './components/GoogleAnalytics';
import MicrosoftClarity from './components/MicrosoftClarity';
import PlanDetailShare from './share/pages/PlanDetailShare';
import CopyItinerary from './pages/CopyItinerary';
import AdminDashboard from './pages/admin/AdminDashboard';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import EmailVerificationRequired from './pages/EmailVerificationRequired';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Pricing from './pages/Pricing';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider } from './contexts/AuthContext';
import { SavingProvider, useSaving } from './contexts/SavingContext';
import { DebugProvider } from './contexts/DebugContext';
import { GoogleOAuthProvider } from '@react-oauth/google';
import './styles/global.css';
import './styles/print.css';

// BackToTopWithSaving component connects BackToTop to SavingContext
function BackToTopWithSaving() {
  const { isSaving } = useSaving();
  return <BackToTop isSaving={isSaving} />;
}

function App() {

  // Add StatCounter analytics
  useEffect(() => {
    // Set StatCounter variables
    window.sc_project = 13161433;
    window.sc_invisible = 1;
    window.sc_security = "8d780fa2";
    
    // Load StatCounter script
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = 'https://www.statcounter.com/counter/counter.js';
    script.async = true;
    document.head.appendChild(script);
    
    // Add noscript fallback to body
    const noscript = document.createElement('noscript');
    noscript.innerHTML = `
      <div class="statcounter">
        <a title="site stats" href="https://statcounter.com/" target="_blank">
          <img class="statcounter" 
               src="https://c.statcounter.com/13161433/0/8d780fa2/1/" 
               alt="site stats" 
               referrerPolicy="no-referrer-when-downgrade" />
        </a>
      </div>
    `;
    document.body.appendChild(noscript);
    
    // Cleanup function
    return () => {
      // Remove script and noscript when component unmounts
      const existingScript = document.querySelector('script[src="https://www.statcounter.com/counter/counter.js"]');
      if (existingScript) {
        existingScript.remove();
      }
      const existingNoscript = document.querySelector('.statcounter');
      if (existingNoscript && existingNoscript.parentNode) {
        existingNoscript.parentNode.remove();
      }
    };
  }, []);

  return (
    <HelmetProvider>
      <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID} locale="en">
        <AuthProvider>
          <DebugProvider>
            <SavingProvider>
              <Router>
                <div className="app-container">
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/email-verification-required" element={<EmailVerificationRequired />} />
                <Route 
                  path="/pricing" 
                  element={
                    <ProtectedRoute>
                      <Pricing />
                    </ProtectedRoute>
                  } 
                />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route 
                  path="/trip-creator" 
                  element={
                    <ProtectedRoute>
                      <CreateTripV2 />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/plans" 
                  element={
                    <ProtectedRoute>
                      <Plans />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/settings" 
                  element={
                    <ProtectedRoute>
                      <Settings />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/groups" 
                  element={
                    <ProtectedRoute>
                      <Groups />
                    </ProtectedRoute>
                  }
                />
                <Route 
                  path="/inbox" 
                  element={
                    <ProtectedRoute>
                      <Inbox />
                    </ProtectedRoute>
                  }
                />
                <Route 
                  path="/inbox-contact-us" 
                  element={
                    <ProtectedRoute>
                      <InboxContactUs />
                    </ProtectedRoute>
                  }
                />
                <Route 
                  path="/luggage-tags" 
                  element={
                    <ProtectedRoute>
                      <LuggageTags />
                    </ProtectedRoute>
                  }
                />
                <Route 
                  path="/luggage-tags/print" 
                  element={
                    <ProtectedRoute>
                      <LuggageTagsPrint />
                    </ProtectedRoute>
                  }
                />
                <Route 
                  path="/plan-detail/:planId"
                  element={
                    <ProtectedRoute>
                      <PlanDetail />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/print/plan/:planId" 
                  element={<PlanPrint />} 
                />
                <Route 
                  path="/share/plan/:planId" 
                  element={<PlanDetailShare />} 
                />
                <Route 
                  path="/copy-itinerary" 
                  element={
                    <ProtectedRoute>
                      <CopyItinerary />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/debug-settings" 
                  element={
                    <ProtectedRoute>
                      <DebugSettings />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin" 
                  element={
                    <ProtectedRoute>
                      <AdminDashboard />
                    </ProtectedRoute>
                  } 
                />
                <Route path="/map-test" element={<MapTest />} />
                <Route path="/date-time-test" element={<DateTimeTest />} />
                <Route 
                  path="/image-search-test" 
                  element={
                    <ProtectedRoute>
                      <ImageSearchTest />
                    </ProtectedRoute>
                  } 
                />
                {/* Catch-all route: redirect any non-existent route to homepage */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              <BackToTopWithSaving />
                <GoogleAnalytics />
                <MicrosoftClarity />
                </div>
              </Router>
            </SavingProvider>
          </DebugProvider>
        </AuthProvider>
      </GoogleOAuthProvider>
    </HelmetProvider>
  );
}

export default App; 